import { camelCase, differenceBy, flatten, isError, last } from 'lodash';
import { makeAutoObservable } from 'mobx';
import config from '../../../config';
import differenceBetweenObjects from '../../../utils/helpers/differenceBetweenObjects';
import SubstageClass from './Substage.class';

const SCHEMA = {
    id: null,
    name: '',
    order: null,
    players: null,
    stage_category: '',
    stage_type: '',
    tournament: null,
    rating_stage: true,
    has_third_place_match: true
};

const DEFAULT_VALUES = {
    ...SCHEMA,
    substages: null,
    inited: false
};

const STAGE_TYPES = config.references.stageTypes.reduce((acc, cur) => {
    acc[camelCase(cur.id)] = cur.id;
    return acc;
}, {});

class StageClass {
    substages = null;

    _inited = false;

    get inited() {
        return this._inited;
    }

    set inited(value) {
        this._inited = value;
    }

    constructor(api, values = {}, parent, autoInit = false) {
        this.api = api;
        this.parent = parent;
        const players = this.findPlayersFromParticipants(values.players);

        const initialValues = { ...SCHEMA, ...values, players };
        this.initialValues = initialValues;

        Object.entries({ ...DEFAULT_VALUES, ...initialValues }).forEach(([key, value]) => {
            this[key] = value;
        });
        this.players = players;

        if (autoInit && this.id) {
            this.init();
        }

        makeAutoObservable(this);
    }

    get basicSubstages() {
        return (
            (this.substages &&
                this.substages.filter((substage) =>
                    ['other', 'semifinal', 'final'].includes(substage.type)
                )) ||
            []
        );
    }

    get otherSubstages() {
        return (
            (this.substages &&
                this.substages.filter(
                    (substage) => !['other', 'semifinal', 'final'].includes(substage.type)
                )) ||
            []
        );
    }

    get mockOtherSubstages() {
        if (this.otherSubstages?.length) {
            return [];
        }

        if (this.has_third_place_match) {
            const MOCK = {
                name: 'Турнир за 3-е место',
                order: 99,
                type: 'third_place',
                players_in_order: null,
                tournament_stage: this.id,
                players: []
            };
            const mocked = new SubstageClass(this.api, this, MOCK);
            mocked.addMatch({
                request_1: null,
                request_2: null,
                id: `${this.id}-MOCK-third-place`
            });
            return [mocked];
        }

        return [];
    }

    get mockSubstages() {
        if (this.stage_type === 'playoff') {
            const lastSubstage = last(this.substages);
            if (!lastSubstage) return [];

            const lastSubstagePlayersCount = lastSubstage.players.length;
            const roundsLeft = Math.floor(Math.log2(lastSubstagePlayersCount)) - 1;
            const acc = [];

            for (let i = 0; i < roundsLeft; i += 1) {
                const prevStage = this.substages.find((el) => el.order === lastSubstage.order - i);

                const nextStageCandidates =
                    prevStage?.matches?.reduce((acc, curr, idx) => {
                        if (curr.winner) {
                            acc.push({ winner: curr.winner, idx });
                        }
                        return acc;
                    }, []) || [];

                const playersInThisRound = Math.ceil(lastSubstagePlayersCount / 2 ** (i + 1));
                const mockedMatchesCount = Math.ceil(playersInThisRound / 2);

                let type = 'other';
                if (i === roundsLeft - 1) {
                    type = 'final';
                } else if (i === roundsLeft - 2) {
                    type = 'semifinal';
                }

                const MOCK = {
                    name: mockedMatchesCount === 1 ? 'Финал' : `1/${mockedMatchesCount} финала`,
                    order: lastSubstage.order + 1 + i,
                    type,
                    players_in_order: null,
                    tournament_stage: this.id,
                    players: []
                };
                const mocked = new SubstageClass(this.api, this, MOCK);

                for (let j = 0; j < mockedMatchesCount; j += 1) {
                    const candidate1 =
                        nextStageCandidates.find((el) => el.idx === j * 2)?.winner || null;
                    const candidate2 =
                        nextStageCandidates.find((el) => el.idx === j * 2 + 1)?.winner || null;

                    mocked.addMatch({
                        request_1: candidate1,
                        request_2: candidate2
                    });
                }
                acc.push(mocked);
            }

            return acc;
        }
        return [];
    }

    get getSubstagesWithMock() {
        return [
            ...this.basicSubstages,
            ...this.mockSubstages,
            ...this.otherSubstages,
            ...this.mockOtherSubstages
        ];
    }

    get getData() {
        return Object.keys(SCHEMA).reduce((acc, curr) => {
            acc[curr] = this[curr];
            return acc;
        }, {});
    }

    // TODO:  условия
    get canDeterminePlaces() {
        switch (this.stage_type) {
            case STAGE_TYPES.playoff:
                return !!this.id;
            case STAGE_TYPES.single:
                return !!this.id;
            default:
                return !!this.id;
        }
    }

    // TODO:  условия: не завершен прием заявок, турнир закрыт
    get canAutoProcessStage() {
        switch (this.stage_type) {
            case STAGE_TYPES.playoff:
                return !!this.id;
            case STAGE_TYPES.single:
                return !!this.id;
            default:
                return !!this.id;
        }
    }

    get canUploadTable() {
        return !!this.id; // TODO:  условия
    }

    get usedPlayers() {
        return flatten(this?.substages.map((substage) => substage.players)) || [];
    }

    get unusedPlayers() {
        return differenceBy(this.players, this.usedPlayers, 'id');
    }

    findPlayersFromParticipants = (ids) => {
        // TODO: Сделать проверку на arrayOf
        if (!this.inited && ids) {
            if (typeof ids[0] === 'object') {
                return ids;
            }
            return ids.map((id) => this.parent.participants.find((player) => id === player.id));
        }
        return this.players;
    };

    init = async (enableReinit = false) => {
        // this.inited = false;
        if (!this.inited || enableReinit) {
            await this.loadSubstages();
            // await this.loadStagePlaces();
        }
        this.inited = true;
    };

    loadThisStage = async () => {
        if (this.id) {
            const stage = this.api.getTournamentStage(this.id);
            return this.setData(stage);
        }

        console.warn('Не задан id стейджа');
        return null;
    };

    uploadStage = async () => {
        if (this.id) {
            const payload = differenceBetweenObjects(this.getData, this.initialValues);

            if (!Object.keys(payload).length) {
                return { ...this.getData, ok: true, changes: null };
            }

            if (payload.players) {
                payload.players = this.players.map((player) => player.id);
            }
            try {
                const stage = await this.api.patchStage({
                    stageId: this.id,
                    payload
                });
                if (stage) {
                    this.initialValues = { ...stage, players: this.players };
                    return {
                        ...this.setData({ ...stage, players: this.players }),
                        ok: true,
                        changes: payload
                    };
                }
            } catch (error) {
                this.setData({ ...this.initialValues });
                return { ...this.getData, ok: !isError(error), changes: null };
            }
            return { ...this.getData, ok: true, changes: null };
        }
        const stage = await this.api.createTournamentStage({
            ...this.getData,
            players: this.players.map((player) => player.id)
        });
        if (stage) {
            this.initialValues = { ...stage, players: this.players };
            return {
                ...this.setData({ ...stage, players: this.players }),
                ok: true,
                changes: this.getData
            };
        }
    };

    deleteStage = async () => {
        if (this.id) {
            await this.api.deleteStage(this.id);
            this.parent.deleteStage(this.id);
        }
    };

    uploadTable = async () => {};

    setData = (data) => {
        Object.entries(data).forEach(([key, value]) => this.updateField(key, value));
        return this.getData;
    };

    updateField = (key, value) => {
        this[key] = value;
        return this[key];
    };

    setSubstages = (substages) => {
        this.substages = substages;
    };

    deleteSubstage = (id) => {
        this.setSubstages(this.substages.filter((substage) => substage.id !== id));
    };

    newSubstage = () => new SubstageClass(this.api, this, { tournament_stage: this.id });

    addSubstage = (substage) => {
        if (substage.constructor === SubstageClass) {
            this.substages = [...this.substages, substage];
            substage.init(true);
        } else {
            this.substages = [...this.substages, new SubstageClass(this.api, this, substage)];
        }
    };

    loadSubstages = async () => {
        const substages = await this.api.getStageSubstages(this.id);

        this.setSubstages(substages.map((substage) => new SubstageClass(this.api, this, substage)));
        return this.substages;
    };

    initSubstages = async () => Promise.all(this.substages?.map((substage) => substage.init()));

    loadDiffSubstages = async () => {
        const substages = await this.api.getStageSubstages(this.id);
        const tSubstages = this.substages?.map((tSubstages) => tSubstages.id) || [];

        const newSubstages = substages.filter((substage) => !tSubstages.includes(substage.id));

        this.setSubstages(
            [
                ...this.substages,
                ...newSubstages.map((substage) => new SubstageClass(this.api, this, substage))
            ].sort((a, b) => a.id - b.id)
        );
        return this.substages;
    };

    handleAutoProcessStage = async ({ payload = null }) => {
        const response = await this.api.handleAutoProcessStage(this.id, payload);
        if (response) {
            if (payload) {
                this.parent.initialLoad();
            } else {
                this.loadDiffSubstages();
            }
        }
    };

    handleDeterminePlaces = async () => {
        // TODO: ручка возвращает места, но с id внутри стейджа, а не сабстейджа, поэтому не будет работать патч
        // const places = await this.api.handleDeterminePlaces(this.id);
        await this.api.handleDeterminePlaces(this.id);
        this.substages.forEach((substage) => {
            substage.loadSubstagePlaces();
        });
    };
}

export default StageClass;
export { SCHEMA };
